import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Lock, ArrowRight, Check, Spinner } from "@phosphor-icons/react";
import { createCheckoutSession } from "@/lib/api";

const QUICK_TIERS = [
  { key: "starter", name: "Starter", price: "$9", projects: "25 projects" },
  { key: "pro", name: "Pro", price: "$19", projects: "Unlimited projects", highlight: true },
  { key: "team", name: "Team", price: "$34", projects: "Unlimited + audit log" },
];

export default function PaywallDialog({ open, onOpenChange, tier, limit, owned }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  // Quick checkout — defaults to monthly cadence here. Users wanting annual
  // hit "Compare plans" → /pricing for the toggle.
  const startCheckout = async (tierId) => {
    setBusy(tierId);
    try {
      const { url } = await createCheckoutSession({
        tier: tierId,
        cadence: "monthly",
        origin_url: window.location.origin,
      });
      if (url) window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout.");
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-2xl" data-testid="paywall-dialog">
        <DialogHeader>
          <div className="h-11 w-11 rounded-md bg-amber-100 text-amber-700 flex items-center justify-center mb-2">
            <Lock size={20} weight="fill" />
          </div>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            You've reached the {tier} plan limit
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            You own {owned} of {limit} projects on the {tier} plan. Upgrade to unlock more projects plus advanced features.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 my-2">
          {QUICK_TIERS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => startCheckout(t.key)}
              disabled={busy !== null}
              className={`text-left bg-white rounded-md border p-3 transition-colors hover:border-slate-950 disabled:opacity-60 ${t.highlight ? "border-slate-950" : "border-slate-300"}`}
              data-testid={`paywall-tier-${t.key}`}
            >
              <div className="text-[10px] font-mono-ibm uppercase tracking-widest text-slate-500">{t.name}</div>
              <div className="flex items-baseline gap-1 mt-0.5">
                <span className="font-display text-2xl font-black tracking-tight">{t.price}</span>
                <span className="text-[10px] text-slate-500 font-mono-ibm">/mo</span>
              </div>
              <div className="text-[11px] text-slate-600 mt-1 flex items-start gap-1">
                <Check size={10} weight="bold" className="text-emerald-600 mt-0.5 shrink-0" />
                {t.projects}
              </div>
              {busy === t.key && (
                <div className="text-[10px] text-slate-500 mt-2 flex items-center gap-1">
                  <Spinner size={10} className="animate-spin" />
                  Opening checkout…
                </div>
              )}
            </button>
          ))}
        </div>

        <p className="text-[11px] text-slate-500 font-mono-ibm uppercase tracking-widest text-center">
          Click any tier to checkout · prefers annual? Compare plans
        </p>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-md border-slate-300">
            Maybe later
          </Button>
          <Button
            onClick={() => navigate("/pricing")}
            className="rounded-md bg-slate-950 hover:bg-slate-800 text-white"
            data-testid="paywall-compare-plans"
          >
            Compare plans <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
